import { Suspense, lazy, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HERO, SPLINE_SCENE } from "@/data/content";
import { MaskedLine } from "@/components/landing/Motion";
import { scrollToId } from "@/components/landing/NavDock";

const Spline = lazy(() => import("@splinetool/react-spline"));

function CircularLoader({ progress }) {
  const r = 54;
  const c = 2 * Math.PI * r;
  const offset = c - (progress / 100) * c;
  return (
    <div className="flex flex-col items-center gap-6" data-testid="hero-loader">
      <div className="relative h-36 w-36">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(80,232,244,0.12)" strokeWidth="2" />
          <circle
            cx="60"
            cy="60"
            r={r}
            fill="none"
            stroke="#50E8F4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.3s linear", filter: "drop-shadow(0 0 6px rgba(80,232,244,0.6))" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-mono text-2xl font-semibold text-fluor">
          {Math.round(progress)}
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="mt-16 font-mono text-[10px] tracking-[0.3em] text-thinair/50">%</span>
        </div>
      </div>
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-thinair/50">
        Initializing neural core
      </p>
    </div>
  );
}

export default function Hero() {
  const [progress, setProgress] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const startT = Date.now();
    const DURATION = 2600;
    const interval = setInterval(() => {
      const pct = Math.min(99, ((Date.now() - startT) / DURATION) * 100);
      setProgress(pct);
    }, 60);
    const done = setTimeout(() => {
      clearInterval(interval);
      setProgress(100);
      setLoaded(true);
    }, DURATION);
    return () => {
      clearInterval(interval);
      clearTimeout(done);
    };
  }, []);

  const contentReady = loaded;

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex min-h-screen w-full items-center overflow-hidden bg-bc"
    >
      {/* grid background */}
      <div className="grid-bg absolute inset-0 opacity-60" />

      {/* 3D scene */}
      <div className="absolute inset-0" data-testid="spline-wrapper">
        {!error && (
          <Suspense fallback={null}>
            <Spline
              scene={SPLINE_SCENE}
              onError={() => setError(true)}
              style={{ width: "100%", height: "100%" }}
            />
          </Suspense>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-[60vmin] w-[60vmin] rounded-full bg-fluor/10 blur-[120px]" />
          </div>
        )}
      </div>

      {/* readability gradient */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,transparent_0%,rgba(0,22,25,0.55)_70%,rgba(0,22,25,0.92)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-bc to-transparent" />

      {/* Loader overlay */}
      <AnimatePresence>
        {!contentReady && (
          <motion.div
            key="loader"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 z-30 flex items-center justify-center bg-bc"
          >
            <CircularLoader progress={progress} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero content */}
      {contentReady && (
        <div className="relative z-20 mx-auto flex w-full max-w-7xl flex-col px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-6 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-fluor" />
            <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-fluor">
              {HERO.eyebrow}
            </span>
          </motion.div>

          <h1 className="font-headers text-5xl font-black uppercase leading-[0.95] tracking-tighter text-thinair sm:text-6xl md:text-8xl">
            {HERO.headlineLines.map((line, i) => (
              <MaskedLine key={line} delay={0.15 + i * 0.12} className={i === 2 ? "text-fluor" : ""}>
                {line}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-xl font-body text-base leading-relaxed text-thinair/75 md:text-lg"
          >
            {HERO.sub}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-4 max-w-xl font-body text-sm leading-relaxed text-thinair/55"
          >
            {HERO.body}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-10"
          >
            <button
              data-testid="hero-cta"
              onClick={() => scrollToId("contact")}
              className="group relative inline-flex items-center gap-3 border border-fluor bg-transparent px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-fluor transition-colors duration-300 hover:bg-fluor hover:text-bc hover:shadow-[0_0_28px_rgba(80,232,244,0.4)]"
            >
              <span className="text-fluor/60 transition-colors group-hover:text-bc/60">[</span>
              {HERO.cta}
              <span className="text-fluor/60 transition-colors group-hover:text-bc/60">]</span>
            </button>
          </motion.div>
        </div>
      )}

      {/* scroll indicator */}
      {contentReady && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-24 left-1/2 z-20 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-thinair/40">Scroll</span>
          <span className="h-10 w-px bg-gradient-to-b from-fluor to-transparent" />
        </motion.div>
      )}
    </section>
  );
}
